import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const Dropdown = ({
    options = [],
    value,
    onChange,
    placeholder = "Select an option",
    label,
    disabled = false,
    width = "100%",
    size = "md",
    iconLeft,
    className = "",
    style = {},
    multi = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const [listPosition, setListPosition] = useState({ top: 0, left: 0, width: 0 });

    const sizes = {
        sm: { height: "32px", fontSize: "var(--text-sm)", padding: "0 10px" },
        md: { height: "40px", fontSize: "var(--text-base)", padding: "0 12px" },
        lg: { height: "48px", fontSize: "var(--text-md)", padding: "0 14px" },
    };

    const currentSize = sizes[size] || sizes.md;

    // Close dropdown when clicking outside (trigger or portaled list)
    useEffect(() => {
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current?.contains(event.target);
            const inList = listRef.current?.contains(event.target);
            if (!inTrigger && !inList) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Update list position when opening
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setListPosition({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
            });
        }
    }, [isOpen]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            return () => document.removeEventListener("keydown", handleEscape);
        }
    }, [isOpen]);

    const selectedOption = multi ? null : options.find((opt) => opt.value === value);
    const selectedValues = multi ? (Array.isArray(value) ? value : []) : [];

    const containerStyles = {
        position: "relative",
        width: width,
        ...style,
    };

    const triggerStyles = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        height: currentSize.height,
        padding: currentSize.padding,
        paddingLeft: iconLeft ? "36px" : currentSize.padding.split(" ")[1],
        backgroundColor: disabled ? "var(--bg-tertiary)" : "var(--bg-primary)",
        border: `1px solid ${isOpen || isFocused ? "var(--border-focus)" : "var(--border-primary)"}`,
        borderRadius: "var(--radius-md)",
        fontSize: currentSize.fontSize,
        color: (multi ? selectedValues.length > 0 : value) ? "var(--text-primary)" : "var(--text-tertiary)",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "border-color var(--transition-fast)",
        opacity: disabled ? 0.5 : 1,
    };

    const iconStyles = {
        position: "absolute",
        left: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "var(--text-secondary)",
        fontSize: "14px",
        pointerEvents: "none",
    };

    const arrowStyles = {
        color: "var(--text-tertiary)",
        fontSize: "12px",
        transition: "transform var(--transition-fast)",
        transform: isOpen ? "rotate(180deg)" : "rotate(0)",
    };

    const listStyles = {
        position: "fixed",
        top: listPosition.top,
        left: listPosition.left,
        width: listPosition.width,
        backgroundColor: "var(--bg-primary)",
        border: "1px solid var(--border-primary)",
        borderRadius: "var(--radius-md)",
        maxHeight: "200px",
        overflowY: "auto",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        zIndex: 9999,
        pointerEvents: isOpen ? "auto" : "none",
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? "visible" : "hidden",
        transition: "opacity var(--transition-fast)",
    };

    const displayLabel = multi
        ? selectedValues.length === 0
            ? placeholder
            : selectedValues.length === 1
                ? options.find((o) => o.value === selectedValues[0])?.label || "1 selected"
                : `${selectedValues.length} selected`
        : selectedOption?.label || placeholder;

    const handleSelect = (option) => {
        if (multi) {
            const current = Array.isArray(value) ? value : [];
            const next = current.includes(option.value)
                ? current.filter((v) => v !== option.value)
                : [...current, option.value];
            onChange?.(next);
        } else {
            onChange?.(option.value);
            setIsOpen(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={className} style={{ width }}>
            {label && (
                <label
                    style={{
                        display: "block",
                        marginBottom: "var(--space-1)",
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--weight-medium)",
                        color: "var(--text-secondary)",
                    }}
                >
                    {label}
                </label>
            )}

            <div ref={dropdownRef} style={containerStyles}>
                {iconLeft && (
                    <span style={iconStyles}>
                        <i className={`fa ${iconLeft}`} aria-hidden="true" />
                    </span>
                )}

                <div
                    ref={triggerRef}
                    style={triggerStyles}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown}
                    tabIndex={disabled ? -1 : 0}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-disabled={disabled}
                >
                    <span
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {displayLabel}
                    </span>
                    <i className="fa fa-chevron-down" style={arrowStyles} aria-hidden="true" />
                </div>

                {createPortal(
                <div ref={listRef} style={listStyles} role="listbox">
                    {options.length > 0 ? (
                        options.map((option, index) => {
                            const isSelected = multi
                                ? selectedValues.includes(option.value)
                                : option.value === value;
                            return (
                                <div
                                    key={option.value ?? index}
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => handleSelect(option)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "10px 12px",
                                        fontSize: currentSize.fontSize,
                                        color: isSelected ? "var(--text-primary)" : "var(--text-secondary)",
                                        backgroundColor: isSelected ? "var(--accent-subtle)" : "transparent",
                                        fontWeight: isSelected ? "var(--weight-medium)" : "var(--weight-normal)",
                                        cursor: "pointer",
                                        transition: "background-color var(--transition-fast)",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.backgroundColor = "var(--bg-secondary)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = isSelected
                                            ? "var(--accent-subtle)"
                                            : "transparent";
                                    }}
                                >
                                    {multi && (
                                        <i
                                            className={`fa ${isSelected ? "fa-check-square" : "fa-square-o"}`}
                                            style={{ fontSize: "14px", color: isSelected ? "var(--accent)" : "var(--text-tertiary)", width: "16px" }}
                                        />
                                    )}
                                    {option.label || option.value}
                                </div>
                            );
                        })
                    ) : (
                        <div
                            style={{
                                padding: "10px 12px",
                                color: "var(--text-tertiary)",
                                fontSize: currentSize.fontSize,
                            }}
                        >
                            No options available
                        </div>
                    )}
                </div>,
                document.body
                )}
            </div>
        </div>
    );
};

export default Dropdown;
